import { CustomEventType } from '@/events/CustomEventRegistry';
import { InputEventDetails } from '@/types/types';
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('increase-decrease-number')
export class IncreaseDecreaseNumber extends LitElement {
	@property({ type: Number }) value = 3;
	@property({ type: Number }) min: number | null = 0;
	@property({ type: Number }) max: number | null = null;
	@property({ type: Boolean }) disabled: boolean = false;
	@property({ type: String }) inputEventIdentifier: string | null = null;
	@property({ type: String }) label: string = 'Runden';
	@property({ type: String }) name: string = 'number-of-rounds';
	@property({ type: String }) additionalIdentifier: string = 'increase-decrease-number';

	static styles = css`
    .value-button {
      display: inline-block;
      border: 1px solid #ddd;
      margin: 0px;
      width: 40px;
      height: 20px;
      text-align: center;
      vertical-align: middle;
      padding: 11px 0;
      background: #eee;
      user-select: none;
      font-family: var(--font-text);
    }

    .value-button:hover {
      cursor: pointer;
    }

    .value-button.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    input#number:disabled {
      background: #f3f4f6;
      cursor: not-allowed;
    }

    form #decrease {
      border-radius: 8px 0 0 8px;
    }

    form #increase {
      border-radius: 0 8px 8px 0;
    }

    input#number {
      text-align: center;
      border: none;
      border-top: 1px solid #ddd;
      border-bottom: 1px solid #ddd;
      width: 40px;
      height: 40px;
      font-family: var(--font-text);
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;

	private increaseValue() {
		if (this.disabled) return;
		const nextValue = Number(this.value) + 1;
		if (this.max === null || nextValue <= this.max) {
			this.value = nextValue;
			this.dispatchValueChange();
		}
	}

	private decreaseValue() {
		if (this.disabled) return;
		const nextValue = Number(this.value) - 1;
		if (this.min === null || nextValue >= this.min) {
			this.value = nextValue;
			this.dispatchValueChange();
		}
	}

	private handleInput(e: Event) {
		if (this.disabled) return;
		const target = e.target as HTMLInputElement;
		const parsed = parseInt(target.value, 10);
		const raw = Number.isFinite(parsed) ? parsed : 0;
		const clampedMin = this.min === null ? raw : Math.max(raw, this.min);
		const clamped = this.max === null ? clampedMin : Math.min(clampedMin, this.max);
		this.value = clamped;
		this.dispatchValueChange();
	}

	private dispatchValueChange() {
		this.dispatchEvent(new CustomEvent<InputEventDetails>(CustomEventType.INPUT_CHANGE, {
			detail: {
				additionalIdentifier: this.inputEventIdentifier ?? this.additionalIdentifier,
				label: this.label,
				name: this.name,
				value: this.value.toString(),
			},
			bubbles: true,
			composed: true
		}));
	}
	//
	render() {
		const decreaseDisabled = this.disabled || (this.min !== null && this.value <= this.min);
		const increaseDisabled = this.disabled || (this.max !== null && this.value >= this.max);
		return html`
      		<form @submit=${(e: Event) => e.preventDefault()} style="display: flex;">
        		<div class="value-button ${decreaseDisabled ? 'disabled' : ''}" id="decrease" @click=${this.decreaseValue}>-</div>
        			<input
          				type="number"
          				id="number"
          				.value=${String(this.value)}
          				@input=${this.handleInput}
						?disabled=${this.disabled}
        			/>
        		<div class="value-button ${increaseDisabled ? 'disabled' : ''}" id="increase" @click=${this.increaseValue}>+</div>
      		</form>
    	`;
	}
}

export default IncreaseDecreaseNumber;